
import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { motion } from "framer-motion";

import Navbar from "@/components/site/Navbar";
import Footer from "@/components/site/Footer";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const SUPPORT_EMAIL = "support@taptotable.co";

const STRIPE_PAYMENT_LINK =
  "https://buy.stripe.com/bJeaEXfdo3EDd1C9i83Nm01";

const activationBenefits = [
  "Your hosted TapToTable Page",
  "Secure, Easy-to-Use TapToTable Dashboard",
  "Create and update Promotions, Events, and Featured Items anytime",
  "Update restaurant details, photos, links, and customer actions",
  "Customer activity and supported analytics",
  "Restaurant photo hosting + TapToTable hosting",
  "Ongoing support",
  "Cancel anytime",
];

export default function OwnerPreview() {
  const { slug } = useParams();

  const [preview, setPreview] = useState(null);
  const [state, setState] = useState("loading");

  useEffect(() => {
    let cancelled = false;

    const loadPreview = async () => {
      try {
        const response = await fetch(`${API}/mockups/by-slug/${slug}`);

        if (response.status === 404) {
          if (!cancelled) setState("notfound");
          return;
        }

        if (!response.ok) {
          throw new Error(`HTTP ${response.status}`);
        }

        const body = await response.json();

        if (!cancelled) {
          setPreview(body);
          setState("ready");
        }
      } catch (error) {
        console.error("Failed to load TapToTable Page:", error);

        if (!cancelled) {
          setState("error");
        }
      }
    };

    loadPreview();

    return () => {
      cancelled = true;
    };
  }, [slug]);

  if (state === "loading") {
    return (
      <div className="min-h-screen bg-[#09090B] text-white">
        <Navbar />
        <main className="flex min-h-[75vh] items-center justify-center px-6 pt-24">
          <div className="text-center">
            <div className="mx-auto h-8 w-8 animate-spin rounded-full border-2 border-[#00E5FF] border-t-transparent" />
            <p className="mt-4 text-sm text-zinc-400">
              Loading your TapToTable Page...
            </p>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  if (state === "notfound") {
    return (
      <div className="min-h-screen bg-[#09090B] text-white">
        <Navbar />
        <main className="flex min-h-[75vh] items-center justify-center px-6 pt-24">
          <div className="max-w-lg text-center">
            <div className="label-caps">Page Unavailable</div>
            <h1 className="mt-3 font-display text-3xl font-semibold">
              This TapToTable Page could not be found
            </h1>
            <p className="mt-4 text-sm leading-relaxed text-zinc-400">
              The review link may have expired or may no longer be available.
              Contact TapToTable if you believe you received this message
              by mistake.
            </p>
            <a
              href={`mailto:${SUPPORT_EMAIL}`}
              className="mt-7 inline-flex rounded-full bg-[#00E5FF] px-6 py-3 text-sm font-semibold text-black transition-colors hover:bg-[#66FFFF]"
            >
              Contact TapToTable
            </a>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  if (state === "error" || !preview) {
    return (
      <div className="min-h-screen bg-[#09090B] text-white">
        <Navbar />
        <main className="flex min-h-[75vh] items-center justify-center px-6 pt-24">
          <div className="max-w-lg text-center">
            <div className="label-caps">Something Went Wrong</div>
            <h1 className="mt-3 font-display text-3xl font-semibold">
              We couldn&apos;t load your TapToTable Page
            </h1>
            <p className="mt-4 text-sm leading-relaxed text-zinc-400">
              Refresh the page and try again. Contact TapToTable if the
              problem continues.
            </p>
            <button
              type="button"
              onClick={() => window.location.reload()}
              className="mt-7 inline-flex rounded-full bg-[#00E5FF] px-6 py-3 text-sm font-semibold text-black transition-colors hover:bg-[#66FFFF]"
            >
              Try Again
            </button>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  const restaurantName =
    preview.restaurant_name || "Your Restaurant";

  const campaignType =
    String(preview.campaign_type || "offer").trim().toLowerCase();

  const isEvent = campaignType === "event";
  const isFeaturedItem = campaignType === "featured_item";
  const isOffer = !isEvent && !isFeaturedItem;

  const campaignLabel = isEvent
    ? "Event"
    : isFeaturedItem
    ? "Featured Item"
    : "Promotion / Offer";

  const previewReviewItems = [
    isEvent
      ? "Review the event details, date, time, and main action"
      : isFeaturedItem
      ? "Review the featured item, photo, description, and main action"
      : "Claim the featured promotion and review the After-Claim Page",
    "Test the customer action buttons",
    "Review the restaurant information and photos",
    "Check the menu, ordering, call, directions, and website links",
  ];

  const previewUrl = `/mockup/${slug}?reset=1`;

  const activationUrl =
    preview.payment_link || STRIPE_PAYMENT_LINK;

  const revisionSubject = encodeURIComponent(
    `TapToTable Page Changes — ${restaurantName}`
  );

  const revisionBody = encodeURIComponent(
    `Hello TapToTable,\n\nI reviewed my ${campaignLabel} TapToTable Page for ${restaurantName}.\n\nHere are the changes I would like:\n\n`
  );

  const revisionEmailUrl =
    `mailto:${SUPPORT_EMAIL}` +
    `?subject=${revisionSubject}` +
    `&body=${revisionBody}`;

  const locationsSubject = encodeURIComponent(
    `Additional TapToTable Locations — ${restaurantName}`
  );

  const locationsBody = encodeURIComponent(
    `Hello TapToTable,\n\nI would like to add another restaurant location under the same ownership as ${restaurantName}.\n\nPlease contact me about the $39/month additional-location rate.\n\nAdditional restaurant details:\n\n`
  );

  const locationsEmailUrl =
    `mailto:${SUPPORT_EMAIL}` +
    `?subject=${locationsSubject}` +
    `&body=${locationsBody}`;

  return (
    <div
      className="min-h-screen bg-[#09090B] text-white"
      data-testid="owner-preview-page"
    >
      <Navbar />

      <main className="px-6 pb-24 pt-32 md:px-12">
        <div className="mx-auto max-w-6xl">
          <motion.section
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.55 }}
            className="text-center"
          >
            <div className="label-caps">
              Your TapToTable Page
            </div>

            <h1
              className="mx-auto mt-3 max-w-4xl font-display text-3xl font-semibold leading-[1.08] tracking-tight sm:text-4xl lg:text-5xl"
              data-testid="owner-preview-heading"
            >
              See Your Restaurant&apos;s TapToTable Page Before You Activate
            </h1>

            <p className="mx-auto mt-5 max-w-2xl text-base leading-relaxed text-zinc-400 md:text-lg">
              We&apos;ve already built this TapToTable Page for{" "}
              <span className="font-medium text-white">
                {restaurantName}
              </span>
              . Review it just as your customers will see it, request any
              changes you&apos;d like, and activate this page when you&apos;re ready.
              You do not need to start over or build another page.
            </p>
          </motion.section>

          <motion.section
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.55, delay: 0.1 }}
            className="mt-10 grid items-start gap-6 lg:grid-cols-12"
          >
            <div className="overflow-hidden rounded-3xl border border-white/10 bg-[#121214] lg:col-span-7">
              <div className="border-b border-white/10 px-5 py-4">
                <div className="flex items-center justify-between gap-4">
                  <div>
                    <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#00E5FF]">
                      Customer Experience
                    </div>

                    <h2 className="mt-1 font-display text-xl font-semibold text-white">
                      {restaurantName}
                    </h2>
                  </div>

                  <span className="rounded-full border border-[#00E5FF]/30 bg-[#00E5FF]/10 px-3 py-1 text-[10px] font-semibold uppercase tracking-[0.14em] text-[#00E5FF]">
                    Ready for Review
                  </span>
                </div>
              </div>

              <div className="p-5 md:p-7">
                <div className="relative aspect-[4/3] overflow-hidden rounded-2xl border border-white/10 bg-black">
                  {preview.hero_image_url ? (
                    <img
                      src={preview.hero_image_url}
                      alt={restaurantName}
                      className="absolute inset-0 h-full w-full object-cover"
                      onError={(event) => {
                        event.currentTarget.style.display = "none";
                      }}
                    />
                  ) : (
                    <div className="absolute inset-0 bg-gradient-to-br from-[#00E5FF]/25 via-zinc-900 to-black" />
                  )}

                  {!isFeaturedItem && (
                    <div className="absolute inset-0 bg-gradient-to-t from-black via-black/15 to-transparent" />
                  )}

                  {!isFeaturedItem && (
                    <div className="absolute bottom-5 left-5 right-5">
                      <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#00E5FF]">
                        Your TapToTable Page
                      </div>

                      <div className="mt-1 font-display text-2xl font-semibold">
                        {restaurantName}
                      </div>

                      {preview.instagram_handle && (
                        <div className="mt-1 text-xs text-zinc-300">
                          @{String(preview.instagram_handle).replace(/^@/, "")}
                        </div>
                      )}
                    </div>
                  )}
                </div>

                {isFeaturedItem && (
                  <div className="mt-4">
                    <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-amber-300">
                      Featured Item
                    </div>
                    <div className="mt-2 font-display text-xl font-semibold text-white">
                      {restaurantName}
                    </div>
                    {preview.instagram_handle && (
                      <div className="mt-1 text-xs text-zinc-400">
                        @{String(preview.instagram_handle).replace(/^@/, "")}
                      </div>
                    )}
                  </div>
                )}

                {isEvent ? (
                  <div className="mt-5 rounded-2xl border border-violet-400/30 bg-violet-400/10 p-5">
                    <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-violet-300">
                      Featured Event
                    </div>

                    <div className="mt-2 font-display text-lg font-semibold leading-snug text-white">
                      {preview.event_name || "Restaurant Event"}
                    </div>

                    {(preview.event_date || preview.event_time) && (
                      <div className="mt-2 text-sm text-zinc-300">
                        {[preview.event_date, preview.event_time]
                          .filter(Boolean)
                          .join(" · ")}
                      </div>
                    )}

                    {preview.event_description && (
                      <p className="mt-2 text-sm leading-relaxed text-zinc-400">
                        {preview.event_description}
                      </p>
                    )}
                  </div>
                ) : isFeaturedItem ? (
                  <div className="mt-5 rounded-2xl border border-amber-400/30 bg-amber-400/10 p-5">
                    <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-amber-300">
                      Featured Menu Item
                    </div>

                    <div className="mt-2 font-display text-lg font-semibold leading-snug text-white">
                      {preview.featured_item_name ||
                        "Your Featured Menu Item"}
                    </div>

                    {preview.featured_item_description && (
                      <p className="mt-2 text-sm leading-relaxed text-zinc-300">
                        {preview.featured_item_description}
                      </p>
                    )}

                    <p className="mt-3 text-xs leading-relaxed text-zinc-400">
                      Customers can explore this item without a discount
                      or claim code.
                    </p>
                  </div>
                ) : isOffer && preview.offer ? (
                  <div className="mt-5 rounded-2xl border border-[#00E5FF]/30 bg-[#00E5FF]/10 p-5">
                    <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#00E5FF]">
                      Featured Promotion
                    </div>

                    <div className="mt-2 font-display text-lg font-semibold leading-snug text-white">
                      {preview.offer}
                    </div>

                    {preview.description && (
                      <p className="mt-2 text-sm leading-relaxed text-zinc-400">
                        {preview.description}
                      </p>
                    )}
                  </div>
                ) : null}

                <Link
                  to={previewUrl}
                  target="_blank"
                  rel="noreferrer"
                  className="mt-5 inline-flex w-full items-center justify-center rounded-2xl bg-[#00E5FF] px-5 py-4 text-sm font-semibold text-black transition-all hover:bg-[#66FFFF] hover:shadow-[0_0_30px_rgba(0,229,255,0.35)]"
                  data-testid="open-customer-preview-button"
                >
                  View Your Customer Page
                </Link>

                <p className="mt-3 text-center text-xs leading-relaxed text-zinc-500">
                  Your customer page opens in a new tab and resets to the
                  beginning each time so you can experience it exactly as a
                  first-time visitor would. After reviewing it, return to this
                  page to request changes or activate.
                </p>
              </div>
            </div>

            <div className="space-y-6 lg:col-span-5">
              <div className="rounded-3xl border border-[#00E5FF]/30 bg-[#00E5FF]/10 p-6">
                <div className="flex items-center gap-3">
                  <span className="inline-flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full bg-[#00E5FF] text-sm font-bold text-black">
                    1
                  </span>

                  <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#00E5FF]">
                    Start Here
                  </div>
                </div>

                <h2 className="mt-4 font-display text-2xl font-semibold">
                  Review Your Customer Page
                </h2>

                <p className="mt-3 text-sm leading-relaxed text-zinc-300">
                  Click the{" "}
                  <span className="font-semibold text-white">
                    View Your Customer Page
                  </span>{" "}
                  button to experience your TapToTable Page exactly as your
                  customers will see it.
                </p>

                <ul className="mt-5 space-y-3">
                  {previewReviewItems.map((item) => (
                    <li
                      key={item}
                      className="flex items-start gap-3 text-sm text-zinc-300"
                    >
                      <span className="mt-0.5 inline-flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-full bg-[#00E5FF]/15 text-xs text-[#00E5FF]">
                        ✓
                      </span>

                      <span>{item}</span>
                    </li>
                  ))}
                </ul>

                <div className="mt-5 rounded-xl border border-[#00E5FF]/20 bg-black/20 p-4">
                  <div className="text-sm font-semibold text-white">
                    When you&apos;re finished
                  </div>

                  <p className="mt-1 text-xs leading-relaxed text-zinc-300">
                    Return to this page to request any changes or activate
                    your TapToTable Page.
                  </p>
                </div>
              </div>

              <div className="rounded-3xl border border-white/10 bg-[#121214] p-6">
                <div className="flex items-center gap-3">
                  <span className="inline-flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full border border-white/15 bg-white/5 text-sm font-bold text-white">
                    2
                  </span>

                  <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-zinc-500">
                    Optional
                  </div>
                </div>

                <h2 className="mt-4 font-display text-xl font-semibold">
                  Need Anything Changed?
                </h2>

                <p className="mt-3 text-sm leading-relaxed text-zinc-400">
                  Click the button below to open a pre-addressed email. Your
                  restaurant name will already be included in the subject line,
                  so you only need to describe what you&apos;d like adjusted.
                </p>

                <p className="mt-3 text-sm leading-relaxed text-zinc-400">
                  You can request updated photos, Promotion, Event, or Featured
                  Item details, restaurant information, customer actions,
                  online ordering links, menu links, or other changes.
                </p>

                <a
                  href={revisionEmailUrl}
                  className="mt-5 inline-flex w-full items-center justify-center rounded-xl border border-white/10 bg-white/5 px-4 py-3 text-sm font-semibold text-white transition-colors hover:bg-white/10"
                  data-testid="request-preview-changes-button"
                >
                  Request Page Changes
                </a>

                <p className="mt-3 text-center text-xs leading-relaxed text-zinc-500">
                  Restaurant in subject: {restaurantName}
                </p>
              </div>
            </div>
          </motion.section>

          <motion.section
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.55, delay: 0.2 }}
            className="mt-6 overflow-hidden rounded-3xl border border-[#00E5FF]/30 bg-[#00E5FF]/10"
          >
            <div className="grid gap-0 lg:grid-cols-12">
              <div className="border-b border-[#00E5FF]/20 p-6 md:p-8 lg:col-span-7 lg:border-b-0 lg:border-r">
                <div className="flex items-center gap-3">
                  <span className="inline-flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full bg-[#00E5FF] text-sm font-bold text-black">
                    3
                  </span>

                  <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#00E5FF]">
                    Ready to Go Live?
                  </div>
                </div>

                <h2 className="mt-5 font-display text-3xl font-semibold md:text-4xl">
                  Activate Your TapToTable Page
                </h2>

                <p className="mt-4 max-w-2xl text-sm leading-relaxed text-zinc-300 md:text-base">
                  When everything looks right, activate this restaurant location
                  for{" "}
                  <span className="font-semibold text-white">
                    $59 per month
                  </span>
                  . Your hosted TapToTable Page, secure, easy-to-use TapToTable
                  Dashboard, Promotions, Events, and Featured Items,
                  supported customer activity analytics, hosting, and
                  ongoing support are included.
                </p>

                <a
                  href={activationUrl}
                  target="_blank"
                  rel="noreferrer"
                  className="mt-6 inline-flex w-full items-center justify-center rounded-xl bg-[#00E5FF] px-5 py-4 text-sm font-semibold text-black transition-all hover:bg-[#66FFFF] hover:shadow-[0_0_30px_rgba(0,229,255,0.35)] sm:w-auto sm:min-w-[320px]"
                  data-testid="activate-preview-button"
                >
                  Activate My TapToTable Page — $59/month
                </a>

                <p className="mt-3 text-xs text-zinc-400">
                  Secure recurring monthly checkout through Stripe.
                  Cancel anytime according to the subscription terms.
                </p>

                <div className="mt-6 rounded-2xl border border-white/10 bg-black/20 p-5">
                  <div className="text-sm font-semibold text-white">
                    Own More Than One Restaurant?
                  </div>

                  <p className="mt-2 text-sm leading-relaxed text-zinc-300">
                    Your first restaurant location is $59/month.
                    Each additional eligible location under the same
                    ownership is $39/month, with its own TapToTable
                    Page, login, and dashboard.
                  </p>

                  <p className="mt-2 text-xs leading-relaxed text-zinc-400">
                    Additional locations are arranged separately after
                    ownership is confirmed. The activation button above
                    is for the standard $59/month subscription only.
                  </p>

                  <a
                    href={locationsEmailUrl}
                    className="mt-4 inline-flex items-center justify-center rounded-xl border border-[#00E5FF]/30 bg-[#00E5FF]/10 px-4 py-3 text-sm font-semibold text-[#66FFFF] transition-colors hover:bg-[#00E5FF]/15"
                  >
                    Ask About Additional Locations
                  </a>
                </div>
              </div>

              <div className="p-6 md:p-8 lg:col-span-5">
                <h3 className="font-display text-xl font-semibold text-white">
                  What happens after activation?
                </h3>

                <p className="mt-2 text-sm leading-relaxed text-zinc-400">
                  We&apos;ll complete your final setup, make your TapToTable
                  Page live, and send your secure, easy-to-use TapToTable
                  Dashboard access so you can manage Promotions, Events,
                  Featured Items, restaurant details, photos, links,
                  customer actions, and supported activity.
                </p>

                <ul className="mt-5 space-y-3">
                  {activationBenefits.map((benefit) => (
                    <li
                      key={benefit}
                      className="flex items-start gap-3 text-sm text-zinc-300"
                    >
                      <span className="mt-0.5 inline-flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-full bg-[#00E5FF]/15 text-xs text-[#00E5FF]">
                        ✓
                      </span>

                      <span>{benefit}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </motion.section>

          <div className="mt-10 text-center">
            <Link
              to="/"
              className="text-sm text-zinc-500 transition-colors hover:text-white"
            >
              ← Back to TapToTable
            </Link>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
}
